/**
 * feAI – Frontend
 * src/pages/ResetPasswordPage.tsx
 *
 * Pagina di scelta della nuova password.
 * Raggiunta tramite:
 *   • Link email:  /reset-password?token=<uuid>&channel=email
 *   • Flusso SMS:  /reset-password?token=<otp>&channel=sms
 *                  (redirect da ForgotPasswordPage dopo verifica OTP)
 *
 * Step:
 *   [form]    → form nuova password + conferma
 *   [success] → messaggio successo + link login
 *   [invalid] → token mancante/malformato in query string
 */

import React, { useState, useId } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FuturEnergyLogo } from '../components/brand/FuturEnergyLogo';
import { apiResetPassword } from '../lib/authApi';
import '../styles/login.css';

type PageStep = 'form' | 'success' | 'invalid';

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token   = searchParams.get('token');
  const channel = searchParams.get('channel') as 'email' | 'sms' | null;

  // ── Validazione parametri URL ──────────────────────────────────────────────
  const initialStep: PageStep =
    token && channel && (channel === 'email' || channel === 'sms')
      ? 'form'
      : 'invalid';

  const [pageStep,    setPageStep]    = useState<PageStep>(initialStep);
  const [password,    setPassword]    = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [showPwd,     setShowPwd]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading,   setIsLoading]   = useState(false);
  const [error,       setError]       = useState<string | null>(null);

  const pwdId  = useId();
  const cfmId  = useId();

  // ── Validazione form ───────────────────────────────────────────────────────
  const passwordsMatch  = password === confirm;
  const passwordLongEnough = password.length >= 8;
  const canSubmit = passwordLongEnough && passwordsMatch && password.length > 0;

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !token || !channel) return;

    setError(null);
    setIsLoading(true);

    try {
      await apiResetPassword(token, channel, password);
      setPageStep('success');
    } catch (err) {
      setError((err as Error).message ?? 'Errore durante il reset. Riprova.');
    } finally {
      setIsLoading(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <main className="login-page" aria-label="Nuova password feAI">
      <div className="login-card">

        <div className="login-logo">
          <FuturEnergyLogo variant="positive" size={200} />
        </div>

        {/* ── STEP: token non valido (URL malformato) ───────────────────── */}
        {pageStep === 'invalid' && (
          <>
            <div className="profile-picker-header">
              <div className="profile-picker-icon" aria-hidden="true">⚠️</div>
              <h1 className="login-title">Link non valido</h1>
              <p className="login-subtitle">
                Il link di reset non è valido o è incompleto.
                Richiedi un nuovo link dalla pagina "Password dimenticata".
              </p>
            </div>
            <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
              <Link
                to="/forgot-password"
                className="btn-primary"
                style={{ display: 'inline-flex', textDecoration: 'none', justifyContent: 'center' }}
              >
                Richiedi nuovo link →
              </Link>
            </div>
          </>
        )}

        {/* ── STEP: form nuova password ──────────────────────────────────── */}
        {pageStep === 'form' && (
          <>
            <h1 className="login-title">Scegli una nuova password</h1>
            <p className="login-subtitle">
              La password deve essere di almeno 8 caratteri.
            </p>

            {error && (
              <div className="error-banner" role="alert">
                <span aria-hidden="true">⚠</span>{error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Nuova password */}
              <div className="form-group">
                <label htmlFor={pwdId} className="form-label">Nuova password</label>
                <div className="input-wrapper">
                  <input
                    id={pwdId}
                    type={showPwd ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Almeno 8 caratteri"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    autoFocus
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="input-eye"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? 'Nascondi password' : 'Mostra password'}
                    tabIndex={-1}
                  >
                    {showPwd ? '🙈' : '👁'}
                  </button>
                </div>
                {/* Indicatore lunghezza */}
                {password.length > 0 && !passwordLongEnough && (
                  <p style={{ marginTop: '0.3rem', fontSize: '0.78rem', color: '#dc2626' }}>
                    Ancora {8 - password.length} caratteri necessari
                  </p>
                )}
              </div>

              {/* Conferma password */}
              <div className="form-group">
                <label htmlFor={cfmId} className="form-label">Conferma password</label>
                <div className="input-wrapper">
                  <input
                    id={cfmId}
                    type={showConfirm ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Ripeti la password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                    disabled={isLoading}
                    style={
                      confirm.length > 0 && !passwordsMatch
                        ? { borderColor: '#dc2626' }
                        : {}
                    }
                  />
                  <button
                    type="button"
                    className="input-eye"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? 'Nascondi conferma' : 'Mostra conferma'}
                    tabIndex={-1}
                  >
                    {showConfirm ? '🙈' : '👁'}
                  </button>
                </div>
                {confirm.length > 0 && !passwordsMatch && (
                  <p style={{ marginTop: '0.3rem', fontSize: '0.78rem', color: '#dc2626' }}>
                    Le password non corrispondono
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading || !canSubmit}
              >
                {isLoading
                  ? <><span className="spinner" aria-hidden="true" /> Salvataggio…</>
                  : <>Salva nuova password →</>
                }
              </button>
            </form>
          </>
        )}

        {/* ── STEP: success ─────────────────────────────────────────────── */}
        {pageStep === 'success' && (
          <>
            <div className="profile-picker-header">
              <div
                className="profile-picker-icon"
                aria-hidden="true"
                style={{ fontSize: '1.6rem' }}
              >
                ✅
              </div>
              <h1 className="login-title">Password aggiornata!</h1>
              <p className="login-subtitle">
                La tua nuova password è stata salvata correttamente.
                Ora puoi accedere al portale.
              </p>
            </div>
            <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
              <Link
                to="/login"
                className="btn-primary"
                style={{ display: 'inline-flex', textDecoration: 'none', justifyContent: 'center' }}
              >
                Accedi ora →
              </Link>
            </div>
          </>
        )}

      </div>
    </main>
  );
};
